import React, { useCallback, useEffect, useState } from 'react';
import { Avatar, Box, ButtonBase, Portal, Typography } from '@mui/material';
import cn from 'classnames';
import Lightbox from 'yet-another-react-lightbox';
import useStyles from './styles';
import { Fullscreen as FullscreenIcon } from '@mui/icons-material';
import useBlobUrl from '@dyl/hooks/useBlobUrl';
import useAssetUrl from '@dyl/hooks/useAssetUrl';
import RootPortalRef from '@dyl/constants/RootPortalRef';

/**
 * @typedef Props
 * @property {Blob} [blob]
 * @property {string} [imageId]
 * @property {string} [src]
 * @property {string} [alt]
 */




/**
 * @param {import('@mui/material').ButtonBaseProps & Props} props
 */
export default function ImageMiniPreview({
    blob,
    src: propsSrc,
    imageId,
    className: propsClass,
    alt,
    showImageDimensions = false,
    ...other
}) {
    const classes = useStyles(other);

    const blobSrc = useBlobUrl(blob);
    const assetSrc = useAssetUrl('images', imageId, { delay: 320 });

    const src = propsSrc || blobSrc || assetSrc;

    const [isLightboxOpen, setLightboxOpen] = useState(false);

    return (
        <>
            <ButtonBase
                onClick={() => setLightboxOpen(true)}
                className={cn(classes.root, propsClass)}
                {...other}
            >
                <Avatar
                    src={src}
                    variant='square'
                    className={classes.image}
                    alt={alt}
                >?</Avatar>

                {showImageDimensions && <ImageDimensions src={src}/>}

                <Box className={classes.hoverOverlay}>
                    <FullscreenIcon />
                </Box>
            </ButtonBase>

            {isLightboxOpen && (
                // Render at app root to avoid getting stuck in things like DataGrid
                <Portal container={RootPortalRef.current}>
                    <Lightbox
                        open={isLightboxOpen}
                        close={() => setLightboxOpen(false)}
                        slides={[{ src }]}
                    />
                </Portal>
            )}
        </>
    );
}

function ImageDimensions({ src }) {

    const [dimensions, setDimensions] = useState(null);

    const getImageDimensions = useCallback(async () => {
        if (!src) return;
        const e = new Image();
        e.src = src;
        await e.decode();
        setDimensions({ width: e.width, height: e.height });
    }, [src]);

    useEffect(() => {
        getImageDimensions();
    }, [getImageDimensions]);

    return (
        <Typography>
            {dimensions ? ` [${dimensions.width}x${dimensions.height}]` : ''}
        </Typography>
    )
}